import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Eye, EyeOff, KeyRound, Lock, Phone, ShieldCheck } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { useStore } from "../../context/StoreContext"
import { useDispatch } from "react-redux"
import { userData } from "../../store/actions"
import { requestPasswordResetOTP, resetPasswordWithOTP } from "../../api/auth/post"
import OtpVerificationModal from "./OtpVerificationModal"
import gestockLogo from "../../assets/logo/gestock.png"

const COUNTRY_CODES = [
  { code: "+225", country: "CI", flag: "🇨🇮" },
  { code: "+33", country: "FR", flag: "🇫🇷" },
  { code: "+90", country: "TR", flag: "🇹🇷" },
  { code: "+221", country: "SN", flag: "🇸🇳" },
  { code: "+226", country: "BF", flag: "🇧🇫" },
  { code: "+229", country: "BJ", flag: "🇧🇯" },
  { code: "+228", country: "TG", flag: "🇹🇬" },
  { code: "+237", country: "CM", flag: "🇨🇲" },
]

function splitPhoneFromQuery(rawPhone: string, fallbackCountryCode: string) {
  const normalized = String(rawPhone || "").trim()
  const matchedCountry =
    COUNTRY_CODES.find((item) => normalized.startsWith(item.code)) ||
    COUNTRY_CODES.find((item) => normalized.startsWith(item.code.replace("+", "")))

  if (!matchedCountry) {
    return {
      countryCode: fallbackCountryCode,
      localPhone: normalized,
    }
  }

  const withPlus = normalized.startsWith(matchedCountry.code)
    ? normalized.slice(matchedCountry.code.length)
    : normalized.slice(matchedCountry.code.replace("+", "").length)

  return {
    countryCode: matchedCountry.code,
    localPhone: withPlus,
  }
}

export default function StaffPasswordSetup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, verifyUser } = useAuth()
  const { setCurrentStore } = useStore()
  const dispatch = useDispatch()

  const queryPhone = searchParams.get("phone") || ""
  const queryCountryCode = searchParams.get("countryCode") || "+225"
  const prefills = useMemo(
    () => splitPhoneFromQuery(queryPhone, queryCountryCode),
    [queryPhone, queryCountryCode]
  )

  const [selectedCountryCode, setSelectedCountryCode] = useState(prefills.countryCode)
  const [phone, setPhone] = useState(prefills.localPhone)
  const [otp, setOtp] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [step, setStep] = useState<"request" | "reset">("request")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showOtp, setShowOtp] = useState(false)
  const [otpPhone, setOtpPhone] = useState("")

  useEffect(() => {
    setSelectedCountryCode(prefills.countryCode)
    setPhone(prefills.localPhone)
  }, [prefills])

  const fullPhone = `${selectedCountryCode}${phone}`.trim()
  const currentCountry =
    COUNTRY_CODES.find((item) => item.code === selectedCountryCode) || COUNTRY_CODES[0]

  const finishLogin = async (result: any) => {
    if (result.store) {
      setCurrentStore(result.store)
    }

    verifyUser(result.token, result.store)
    dispatch(userData())

    setSuccess("Compte activé. Connexion en cours...")

    setTimeout(() => {
      window.location.href = "/dashboard"
    }, 1600)
  }

  const handleRequestCode = async () => {
    if (!phone.trim()) {
      setError("Veuillez saisir votre numéro de téléphone")
      return
    }

    setLoading(true)
    setError("")
    setSuccess("")

    try {
      await Promise.all([
        requestPasswordResetOTP(fullPhone),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ])
      setStep("reset")
      setSuccess("Un code OTP a été envoyé. Saisissez-le puis définissez votre mot de passe.")
    } catch (err: any) {
      setError(err?.message || "Impossible d'envoyer le code pour le moment")
    } finally {
      setLoading(false)
    }
  }

  const handleSetPassword = async () => {
    if (otp.trim().length !== 4) {
      setError("Le code OTP doit contenir 4 chiffres")
      return
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères")
      return
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas")
      return
    }

    setLoading(true)
    setError("")
    setSuccess("")

    try {
      await Promise.all([
        resetPasswordWithOTP({ phone: fullPhone, otp, password }),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ])

      const result = await login({ phone: fullPhone, password })

      if (result.requestOtp) {
        setOtpPhone(fullPhone)
        setShowOtp(true)
        setSuccess("Mot de passe enregistré. Vérifiez maintenant votre OTP.")
        return
      }

      await finishLogin(result)
    } catch (err: any) {
      setError(err?.message || "Impossible de définir le mot de passe")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="relative w-full max-w-5xl overflow-hidden rounded-[30px] border border-white/50 bg-white/90 shadow-2xl backdrop-blur-xl md:grid md:grid-cols-[0.95fr_1.05fr]">
        <div className="relative overflow-hidden bg-gradient-to-br from-cyan-600 via-sky-700 to-blue-900 px-6 py-8 text-white sm:px-8 md:p-10">
          <div className="pointer-events-none absolute -left-10 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 right-0 h-44 w-44 rounded-full bg-cyan-200/15 blur-3xl" />

          <div className="relative z-10 flex h-full flex-col justify-between">
            <div>
              <img src={gestockLogo} alt="GESTOCK logo" className="h-20 w-auto" />
              <p className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-white/85">
                <KeyRound size={14} />
                Activation de compte
              </p>
              <h1 className="mt-4 max-w-md text-3xl font-bold leading-tight">
                Définissez votre mot de passe et connectez-vous à votre boutique.
              </h1>
              <p className="mt-3 max-w-md text-sm text-white/85">
                Utilisez le lien reçu après création de votre compte collaborateur pour activer
                votre accès GESTOCK.
              </p>
            </div>

            <div className="mt-8 rounded-3xl border border-white/15 bg-white/10 p-5 backdrop-blur-sm">
              <p className="text-sm font-semibold">Comment ça marche ?</p>
              <div className="mt-4 space-y-3 text-sm text-white/85">
                <p>1. Confirmez votre numéro.</p>
                <p>2. Recevez un code OTP.</p>
                <p>3. Définissez votre nouveau mot de passe.</p>
                <p>4. Connexion automatique à votre espace.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative p-6 sm:p-8 md:p-10">
          <div className="mx-auto w-full max-w-md">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600/80">Nouvel accès</p>
              <h2 className="mt-1 text-2xl font-bold text-slate-900">
                {step === "request" ? "Recevoir le code OTP" : "Créer votre mot de passe"}
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                {step === "request"
                  ? "Un code sera envoyé sur le numéro associé à votre compte."
                  : "Saisissez le code reçu puis choisissez un mot de passe sécurisé."}
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {success}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Numéro de téléphone</label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3">
                    <span className="text-lg leading-none">{currentCountry.flag}</span>
                    <select
                      value={selectedCountryCode}
                      onChange={(e) => setSelectedCountryCode(e.target.value)}
                      disabled={loading || step === "reset"}
                      className="border-none bg-transparent text-sm font-semibold text-slate-700 outline-none focus:ring-0"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Input
                    icon={<Phone size={18} />}
                    value={phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                    placeholder="07 00 00 00 00"
                    inputMode="numeric"
                    autoComplete="tel"
                    disabled={loading || step === "reset"}
                  />
                </div>
              </div>

              {step === "reset" && (
                <>
                  <Input
                    icon={<ShieldCheck size={18} />}
                    value={otp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    placeholder="Code OTP"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    disabled={loading}
                  />

                  <Input
                    icon={<Lock size={18} />}
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    placeholder="Nouveau mot de passe"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    disabled={loading}
                    trailing={
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="text-slate-400 transition hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />

                  <Input
                    icon={<Lock size={18} />}
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmer le mot de passe"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    disabled={loading}
                    trailing={
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="text-slate-400 transition hover:text-slate-600"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />
                </>
              )}

              <button
                onClick={step === "request" ? handleRequestCode : handleSetPassword}
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading
                  ? step === "request"
                    ? "Envoi du code..."
                    : "Activation..."
                  : step === "request"
                    ? "Recevoir le code OTP"
                    : "Créer mon mot de passe"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full text-sm font-medium text-slate-500 transition hover:text-blue-600"
              >
                Retour à la connexion
              </button>
            </div>
          </div>
        </div>
      </div>

      {showOtp && (
        <OtpVerificationModal
          isOpen={showOtp}
          phone={otpPhone}
          onClose={() => setShowOtp(false)}
          onSuccess={async (res: any) => {
            setShowOtp(false)
            await finishLogin(res)
          }}
        />
      )}
    </>
  )
}

function Input({ icon, trailing, className = "", ...props }: any) {
  return (
    <div className={`relative flex-1 ${className}`}>
      <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</div>
      <input
        {...props}
        className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-10 text-[15px] text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
      />
      {trailing && <div className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</div>}
    </div>
  )
}
